// @flow
import * as React from 'react';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import { Box } from 'grommet';
import { isEmpty, isEqual } from 'lodash';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import Point12WithCables from 'esm-web-pro/containers/pages/network/objects/point11/Point12WithCables';
import UseHighlightCable from 'esm-web-pro/containers/pages/network/objects/cable/useHighlightCable';
import { numberPointsCallbacksCable } from 'esm-web-pro/containers/pages/network/objects/cable/MapCable';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';
import ContentLabel from 'esm-web-pro/components/layout/sidePanel/ContentLabel';
import PoPContent from './PoPContent';

type Props = {
    point11: Object,
    cables: Object,
    intl: IntlShape,
    getCableData: Function,
    onDisconnectCable: Function,
    mapIsDraft: boolean,
    editor: BaseEditor,
    onView: Function,

    leaflet: LeafletContext,
    onRowClick: Function,
    clickedRow: ?string,
    sortCables: Function
}

const msg = defineMessages({
    noConnected: {
        id: 'no-connected-cables',
        defaultMessage: 'No connected cables.',
    },
    connected: {
        id: 'connected-cables',
        defaultMessage: 'Connected',
    },
});

const ConnectedCablesList = (props: Props) => {
    const {
        cables, intl: { formatMessage }, onDisconnectCable, mapIsDraft, editor,
        onView, leaflet: { map }, point11, onRowClick, clickedRow, getCableData,
        sortCables,
    } = props;
    const { layers1_2: points12 } = cables;
    const sortedPoints12 = Object.values(points12).sort((prev, next) => prev.sequence - next.sequence);

    const handleClick = (point: Object) => {
        onView();
        const focusCableCallbacks = [];
        const allCoordinates = [];
        const values = Object.values(point.cables);
        const point11Coordinates = [point11.coordinates.coordinates[1], point11.coordinates.coordinates[0]];
        editor.unregisterAllLoadingCallback();
        if (!isEmpty(values) && values.find((v) => !v.patch)) {
            values.forEach((cable: Object) => {
                if (cable.patch) return;
                let path = cable.path.map((c) => [c[1], c[0]]);
                if (!isEqual(point11Coordinates, path[0])) {
                    path = path.reverse();
                }
                editor.registerMultipleLoadingCallbacks(numberPointsCallbacksCable(cable));
                const focusCableCallback = UseHighlightCable(path);
                focusCableCallbacks.push(focusCableCallback);
                allCoordinates.push(...path);
            });
            editor.registerMultipleLoadingCallbacks(focusCableCallbacks);
        } else {
            let number = point11.sequence;
            if (point11.type === 'pop') {
                number = point11.alias;
            }
            const highlightPoint11 = UseHighlightPoint(
                point11.coordinates.coordinates,
                number,
            );
            editor.registerLoadingCallback(highlightPoint11);
        }
        if (map) {
            if (!isEmpty(allCoordinates)) {
                map.highlightedGeometry = {
                    bounds: allCoordinates, center: point11Coordinates,
                };
                map.flyToBoundsCenter(allCoordinates, point11Coordinates);
            } else {
                map.highlightedGeometry = {
                    bounds: null, center: point11Coordinates,
                };
                map.panTo(point11Coordinates);
            }
        }
        editor.reloadFeatures();
        onRowClick(`point-${point.id}`);
    };

    const contentProps = {
        point11,
        onDisconnectCable,
        mapIsDraft,
        editor,
        onClick: handleClick,
        onPoint11View: onView,
        clicked: clickedRow,
        setClicked: onRowClick,
        getCableData,
        sortCables,
    };

    return (
        <React.Fragment key={`connected-cable-list-container-${point11.id}`}>
            <ContentLabel>{formatMessage(msg.connected)}</ContentLabel>
            <Box>
                {(isEmpty(sortedPoints12)) && (
                    <ContentRow padLevel={1}>
                        <ValueText>{formatMessage(msg.noConnected)}</ValueText>
                    </ContentRow>
                )}
                {point11.type === 'pop' ? (
                    <PoPContent points12={sortedPoints12} {...contentProps} />
                ) : Object.keys(sortedPoints12).map((key) => (
                    <Point12WithCables
                        key={`point12-${key}-cables`}
                        point={sortedPoints12[key]}
                        {...contentProps}
                    />
                ))}
            </Box>
        </React.Fragment>
    );
};

export default withLeaflet(injectIntl(React.memo<Props>(ConnectedCablesList)));
